import { AodRule } from '@app/modules/audit/modules/aod/aod.types';
import { useAuditReportsListAodRules } from '@mi-front/api-v2';
import { Autocomplete, TextField } from '@mui/material';

/**
 * Props for the AOD Rules Autocomplete component.
 */
export interface AodRulesAutocompleteProps {
  /** The selected rule ID */
  value: string | null;
  /** Callback when the selected rule changes */
  onChange: (ruleId: string | null) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Custom label for the autocomplete */
  label?: string;
  /** Custom placeholder text */
  placeholder?: string;
  /** Custom styles */
  sx?: object;
}

/**
 * Autocomplete component for selecting AOD rules.
 * Fetches all available AOD rules and displays them in an autocomplete dropdown.
 */
export function AodRulesAutocomplete({
  value,
  onChange,
  disabled = false,
  label,
  placeholder,
  sx,
}: AodRulesAutocompleteProps) {
  // Fetch AOD rules with only the fields needed for the autocomplete
  const { data, isLoading } = useAuditReportsListAodRules({
    fields: ['id,name'],
    sort: ['name,ASC'],
    limit: 10000,
  });

  const rules = data?.data || [];
  const selectedRule = rules.find((rule) => rule.id === value) || null;

  const handleChange = (_event: React.SyntheticEvent, newValue: AodRule | null) => {
    onChange(newValue?.id || null);
  };

  return (
    <Autocomplete
      size="small"
      value={selectedRule}
      options={rules}
      loading={isLoading}
      disabled={disabled}
      onChange={handleChange}
      getOptionLabel={(option: AodRule) => option.name}
      isOptionEqualToValue={(option: AodRule, val: AodRule) => option.id === val.id}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      sx={sx}
    />
  );
}
